import { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Download, Lock, Send, UserPlus } from 'lucide-react';
import { toast } from '../components/ui/sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ORDER_TIERS, QUANTITY_OPTIONS, MIN_SIZES_SELECTED, TIRE_CATEGORIES } from '../lib/tireSizes';
import { ORDER } from '../constants/testIds';
import { getErrorMessage as errDetail } from '../lib/errors';
import { setClientToken } from '../lib/authTokens';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DOWNLOAD_URL = `${API}/size-sheet/download`;

const emptyDetails = { phone: '', business: '', city: '', state: '', country: '' };

export const OrderForm = () => {
  // step 1: name, email, request notepad. step 2: optional account + tier/delivery details.
  const [step, setStep] = useState('intro');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [request, setRequest] = useState('');
  const [checkingEmail, setCheckingEmail] = useState(false);

  const [hasAccount, setHasAccount] = useState(false);
  const [isAccountFlow, setIsAccountFlow] = useState(false);
  const [password, setPassword] = useState('');
  const [verifyingPassword, setVerifyingPassword] = useState(false);

  const [tier, setTier] = useState(1600);
  const [form, setForm] = useState(emptyDetails);
  const [submitting, setSubmitting] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [sizesStepPending, setSizesStepPending] = useState(false);
  const [selectedSizes, setSelectedSizes] = useState({});
  const [confirmingSizes, setConfirmingSizes] = useState(false);

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!name || !email || !request) {
      toast.error('Please tell us your name, email and what you need first');
      return;
    }
    setCheckingEmail(true);
    try {
      const res = await axios.post(`${API}/client/interest`, { email });
      setHasAccount(res.data.has_account);
      setStep('details');
    } catch (err) {
      toast.error(errDetail(err, 'Something went wrong. Please try again.'));
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setVerifyingPassword(true);
    try {
      const endpoint = hasAccount ? 'login' : 'register';
      const { data } = await axios.post(`${API}/client/${endpoint}`, { email, password }, { withCredentials: true });
      if (data.access_token) setClientToken(data.access_token);
      setIsAccountFlow(true);
      toast.success(hasAccount ? 'Welcome back! Linked to your account.' : 'Account created — this order will be saved to it.');
    } catch (err) {
      toast.error(errDetail(err, 'Something went wrong. Please try again.'));
    } finally {
      setVerifyingPassword(false);
    }
  };

  const postOrder = async (wantsPdf) => {
    const payload = {
      name,
      phone: form.phone || 'Not provided',
      business_name: form.business || null,
      tier,
      request_details: request,
      wants_pdf: wantsPdf,
      delivery_city: form.city || null,
      delivery_state: form.state || null,
      delivery_country: form.country || null,
    };
    if (isAccountFlow) {
      const res = await axios.post(`${API}/client/orders`, payload, { withCredentials: true });
      return res.data;
    }
    const res = await axios.post(`${API}/orders`, { ...payload, email });
    return res.data;
  };

  const handleSubmitOnline = async (e) => {
    e.preventDefault();
    if (!form.country.trim()) {
      toast.error('Delivery country is required so we can calculate freight');
      return;
    }
    setSubmitting('online');
    try {
      const data = await postOrder(false);
      setSubmitted(data);
      setSizesStepPending(true);
      toast.success(`Request ${data.order_number} sent — we'll be in touch shortly!`);
    } catch (err) {
      toast.error(errDetail(err, 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(null);
    }
  };

  const handleDownload = async () => {
    if (!form.country.trim()) {
      toast.error('Delivery country is required so we can calculate freight');
      return;
    }
    setSubmitting('download');
    try {
      const data = await postOrder(true);
      setSubmitted(data);
      window.open(DOWNLOAD_URL, '_blank');
      toast.success('Your download should start automatically!');
    } catch (err) {
      toast.error(errDetail(err, 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(null);
    }
  };

  const setSizeQty = (size, qty) => {
    setSelectedSizes((prev) => {
      const next = { ...prev };
      if (next[size] === qty) {
        delete next[size];
      } else {
        next[size] = qty;
      }
      return next;
    });
  };

  const confirmSizes = async () => {
    setConfirmingSizes(true);
    try {
      await axios.patch(`${API}/orders/${submitted.order_number}/sizes`, { selected_sizes: selectedSizes });
      toast.success('Your preferred sizes were added to the request!');
    } catch (err) {
      toast.error(errDetail(err, 'Could not save your sizes — our team will confirm them with you directly.'));
    } finally {
      setConfirmingSizes(false);
      setSizesStepPending(false);
    }
  };

  const skipSizes = () => setSizesStepPending(false);

  const resetAll = () => {
    setStep('intro');
    setName('');
    setEmail('');
    setRequest('');
    setHasAccount(false);
    setIsAccountFlow(false);
    setPassword('');
    setTier(1600);
    setForm(emptyDetails);
    setSubmitted(null);
    setSizesStepPending(false);
    setSelectedSizes({});
  };

  const sizesCount = Object.keys(selectedSizes).length;

  if (submitted && sizesStepPending) {
    return (
      <section id="order-form" className="relative bg-brand-bg py-28 sm:py-36">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-widest text-brand-green mb-4 text-center">
            Request {submitted.order_number} Received
          </p>
          <h2 className="font-display font-extrabold uppercase text-3xl sm:text-4xl text-brand-text text-center">
            Know Your Sizes? Select Them Now
          </h2>
          <p className="mt-4 text-brand-muted text-sm sm:text-base text-center max-w-2xl mx-auto">
            How to order: tap the quantity (20, 50 or 200 units) next to every size you want to include. To
            build out a full bulk container order, select at least {MIN_SIZES_SELECTED} different sizes — mix
            and match to reach your target package of 800, 1600 or 2400 total tires. Or skip and our team will
            curate a mix for you based on your package.
          </p>

          <div className="mt-10 rounded-2xl border border-brand-border bg-brand-surface p-5 sm:p-8 space-y-6 max-h-[32rem] overflow-y-auto">
            {TIRE_CATEGORIES.map((cat) => (
              <div key={cat.id}>
                <p className="font-mono text-xs uppercase tracking-wider text-brand-amber mb-2">
                  {cat.label} <span className="text-brand-muted">({cat.rimRange})</span>
                </p>
                <div className="space-y-1.5">
                  {cat.sizes.map((size) => (
                    <div key={size} className="flex items-center justify-between gap-3 py-1 border-b border-brand-border/40 last:border-0">
                      <span className="font-mono text-xs text-brand-text">{size}</span>
                      <div className="flex gap-1.5">
                        {QUANTITY_OPTIONS.map((qty) => {
                          const active = selectedSizes[size] === qty;
                          return (
                            <button
                              key={qty}
                              type="button"
                              data-testid={ORDER.sizeChip(`${size}-${qty}`)}
                              onClick={() => setSizeQty(size, qty)}
                              className={`inline-flex items-center justify-center gap-1 rounded-md border w-14 py-1 font-mono text-[11px] transition-colors ${
                                active
                                  ? 'border-brand-orange bg-brand-orange/15 text-brand-text'
                                  : 'border-brand-border text-brand-muted hover:border-brand-amber hover:text-brand-text'
                              }`}
                            >
                              {active && <Check className="w-3 h-3" />}
                              {qty}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p data-testid={ORDER.sizesSelectedCount} className="text-brand-muted text-sm">
              <span className="text-brand-text font-mono">{sizesCount}</span> of {MIN_SIZES_SELECTED} recommended sizes selected
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={skipSizes}
                data-testid={ORDER.sizesSkipButton}
                className="inline-flex items-center gap-2 border border-brand-border text-brand-text font-display font-bold uppercase tracking-wide rounded-full px-6 py-3 hover:border-brand-amber transition-colors text-sm"
              >
                Skip — Confirm With Team
              </button>
              <button
                type="button"
                onClick={confirmSizes}
                disabled={sizesCount === 0 || confirmingSizes}
                data-testid={ORDER.sizesConfirmButton}
                className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-6 py-3 transition-colors disabled:opacity-60 text-sm"
              >
                {confirmingSizes ? 'Saving…' : 'Confirm My Sizes'}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (submitted) {
    return (
      <section id="order-form" className="relative bg-brand-bg py-28 sm:py-36">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center" data-testid={ORDER.successMessage}>
          <p className="font-mono text-xs uppercase tracking-widest text-brand-green mb-4">Request Received</p>
          <h2 className="font-display font-extrabold uppercase text-3xl sm:text-4xl text-brand-text">Thanks, {submitted.name.split(' ')[0]}!</h2>
          <p className="mt-4 text-brand-muted text-base sm:text-lg">
            Your request (<span className="text-brand-amber font-mono">{submitted.order_number}</span>) for our{' '}
            <span className="text-brand-text font-mono">{submitted.tier}-tire package</span> has been received. Our team will reach
            out to you at {submitted.email} shortly to confirm pricing and availability.
            {isAccountFlow && ' You can track its status anytime from your account.'}
          </p>
          {submitted.wants_pdf && (
            <a
              href={DOWNLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-brand-amber font-mono text-xs uppercase tracking-wider underline"
            >
              Didn't download automatically? Click here
            </a>
          )}
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {isAccountFlow && (
              <a
                href="/account"
                className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3 transition-colors"
              >
                View My Orders
              </a>
            )}
            <button
              type="button"
              data-testid={ORDER.resetButton}
              onClick={resetAll}
              className="inline-flex items-center gap-2 border border-brand-border text-brand-text font-display font-bold uppercase tracking-wide rounded-full px-7 py-3 hover:border-brand-amber transition-colors"
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="order-form" data-testid={ORDER.section} className="relative bg-brand-bg py-24 sm:py-32">
      <div className="max-w-2xl mx-auto px-5 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-widest text-brand-amber mb-3 text-center">Request a Bulk Quote</p>
        <h2 className="font-display font-extrabold uppercase tracking-tight text-brand-text text-3xl sm:text-4xl lg:text-5xl text-center">
          Tell Us What You Need
        </h2>
        <p className="mt-4 text-brand-muted text-base sm:text-lg text-center">
          {step === 'intro'
            ? "Start with your info and what you're looking for."
            : "Almost there — let's finish setting up your order."}
        </p>

        {step === 'intro' && (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            onSubmit={handleContinue}
            className="mt-12 rounded-2xl border border-brand-border bg-brand-surface p-6 sm:p-10 space-y-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-brand-muted text-xs uppercase tracking-wider">Full Name *</Label>
                <Input data-testid={ORDER.nameInput} value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
              </div>
              <div>
                <Label className="text-brand-muted text-xs uppercase tracking-wider">Email *</Label>
                <Input type="email" data-testid={ORDER.emailInput} value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
              </div>
            </div>

            <div>
              <Label className="text-brand-muted text-xs uppercase tracking-wider">What Are You Looking For? *</Label>
              <Textarea
                data-testid={ORDER.requestInput}
                value={request}
                onChange={(e) => setRequest(e.target.value)}
                placeholder="e.g. Mixed passenger & SUV sizes, mostly Grade A/B for a 40ft container — or ask about our new bulk tire loads / international export"
                className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text"
                rows={3}
              />
            </div>

            <button
              type="submit"
              disabled={checkingEmail}
              data-testid={ORDER.continueButton}
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3.5 transition-colors disabled:opacity-60"
            >
              {checkingEmail ? 'Please wait…' : 'Continue'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.form>
        )}

        {step === 'details' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-12 space-y-6">
            {!isAccountFlow ? (
              <form onSubmit={handleAccountSubmit} className="rounded-2xl border border-brand-border/70 bg-brand-surface/60 p-5 sm:p-6 space-y-3">
                <p className="font-mono text-[11px] uppercase tracking-wider text-brand-amber">Optional</p>
                <p className="text-brand-text text-sm">
                  {hasAccount ? 'Welcome back! Enter your password to save this order to your account.' : 'Want to save your info & track this order? Create a free account.'}
                </p>
                <div>
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">
                    {hasAccount ? 'Enter Password' : 'Choose Password'}
                  </Label>
                  <div className="mt-1.5 flex flex-col sm:flex-row gap-3">
                    <Input
                      type="password"
                      placeholder="••••••••"
                      data-testid={ORDER.accountPasswordInput}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="flex-1 bg-brand-surface2 border-brand-border text-brand-text"
                    />
                    <button
                      type="submit"
                      disabled={verifyingPassword}
                      data-testid={ORDER.accountSubmitButton}
                      className="inline-flex items-center justify-center gap-2 border border-brand-border text-brand-text font-display font-bold uppercase tracking-wide rounded-full px-6 py-2.5 hover:border-brand-amber transition-colors disabled:opacity-60 whitespace-nowrap text-sm"
                    >
                      {hasAccount ? <Lock className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
                      {verifyingPassword ? 'Please wait…' : hasAccount ? 'Log In' : 'Create Account'}
                    </button>
                  </div>
                </div>
                <p className="text-brand-muted text-xs">No account needed — just fill out the rest below to continue as a guest.</p>
              </form>

            ) : (
              <p className="font-mono text-xs uppercase tracking-wider text-brand-green">Signed in as {email} — this order will be saved to your account.</p>
            )}

            <form onSubmit={handleSubmitOnline} className="rounded-2xl border border-brand-border bg-brand-surface p-6 sm:p-10 space-y-6">
              <div>
                <Label className="text-brand-muted text-xs uppercase tracking-wider">Which Package Interests You?</Label>
                <div className="mt-2 grid grid-cols-3 gap-3">
                  {ORDER_TIERS.map((t) => (
                    <button
                      key={t.quantity}
                      type="button"
                      data-testid={ORDER.tierButton(t.quantity)}
                      onClick={() => setTier(t.quantity)}
                      className={`rounded-xl border px-3 py-3 text-center transition-colors ${
                        tier === t.quantity ? 'border-brand-orange bg-brand-orange/10' : 'border-brand-border hover:border-brand-amber'
                      }`}
                    >
                      <p className="font-display font-bold text-brand-text text-lg">{t.quantity}</p>
                      <p className="text-brand-muted text-[11px] font-mono uppercase">{t.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">Business Name</Label>
                  <Input data-testid={ORDER.businessInput} value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                </div>
                <div>
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">Phone</Label>
                  <Input type="tel" data-testid={ORDER.phoneInput} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">Delivery City</Label>
                  <Input data-testid={ORDER.cityInput} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                </div>
                <div>
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">Delivery State</Label>
                  <Input data-testid={ORDER.stateInput} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                </div>
                <div>
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">Delivery Country *</Label>
                  <Input data-testid={ORDER.countryInput} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submitting !== null}
                  data-testid={ORDER.submitOnlineButton}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3.5 transition-colors disabled:opacity-60"
                >
                  <Send className="w-4 h-4" />
                  {submitting === 'online' ? 'Sending…' : 'Submit Request Online'}
                </button>
                <button
                  type="button"
                  disabled={submitting !== null}
                  onClick={handleDownload}
                  data-testid={ORDER.downloadButton}
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-brand-border text-brand-text font-display font-bold uppercase tracking-wide rounded-full px-7 py-3.5 hover:border-brand-amber transition-colors disabled:opacity-60"
                >
                  <Download className="w-4 h-4" />
                  {submitting === 'download' ? 'Preparing…' : 'Or Download the Order Form'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </div>
    </section>
  );
};
